import { useEffect, useState } from "react";
import { getExpenses, deleteExpense } from "../../../services/expenseApi";
import "./ExpenseList.css";

const MONTHS = [...Array(12)].map((_, i) =>
  ({ value: i + 1, label: new Date(0, i).toLocaleString("default", { month: "long" }) })
);
const YEARS = [2023, 2024, 2025, 2026];

export default function ExpenseList() {
  const [data, setData] = useState({
    expenses: [], total_income: 0, total_expense: 0,
    previous_balance: 0, balance: 0,
  });
  const [filter, setFilter] = useState({
    month: new Date().getMonth() + 1,
    year:  new Date().getFullYear(),
  });
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getExpenses({ month: filter.month, year: filter.year });
      const d = res.data;
      setData({
        expenses:         Array.isArray(d.entries) ? d.entries : [],
        total_income:     Number(d.summary?.total_income    || 0),
        total_expense:    Number(d.summary?.total_expense   || 0),
        previous_balance: Number(d.summary?.previous_balance || 0),
        balance:          Number(d.summary?.balance         || 0),
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [filter]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await deleteExpense(id); loadData(); }
    catch (e) { console.error(e); }
  };

  const incomes  = data.expenses.filter(e => e.type === "income");
  const expenses = data.expenses.filter(e => e.type === "expense");
  const maxRows  = Math.max(incomes.length, expenses.length);

  const monthLabel = new Date(filter.year, filter.month - 1)
    .toLocaleString("default", { month: "long", year: "numeric" });

  return (
    <div className="fin-page">

      {/* ══ HERO ══ */}
      <div className="fin-hero">
        <div className="fin-hero-left">
          <div className="fin-hero-icon">💰</div>
          <div>
            <h2 className="fin-hero-title">Income &amp; Expense Tracker</h2>
            <p className="fin-hero-sub">{monthLabel}</p>
          </div>
        </div>
        <div className="fin-hero-right">
          <a href="/school-admin/expense/add" className="fin-btn-add">+ Add Entry</a>
        </div>
      </div>

      {/* ══ FILTER ══ */}
      <div className="fin-filter">
        <span className="fin-filter-label">Filter by</span>
        <select value={filter.month} onChange={e => setFilter({ ...filter, month: Number(e.target.value) })}>
          {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={filter.year} onChange={e => setFilter({ ...filter, year: Number(e.target.value) })}>
          {YEARS.map(y => <option key={y}>{y}</option>)}
        </select>
      </div>

      {/* ══ SUMMARY CARDS ══ */}
      <div className="fin-summary">
        <div className="fin-stat balance-prev">
          <span className="fin-stat-icon">🗓️</span>
          <p className="fin-stat-label">Previous Balance</p>
          <p className="fin-stat-value">₹ {data.previous_balance.toFixed(2)}</p>
        </div>
        <div className="fin-stat income">
          <span className="fin-stat-icon">📈</span>
          <p className="fin-stat-label">Total Income</p>
          <p className="fin-stat-value">₹ {data.total_income.toFixed(2)}</p>
        </div>
        <div className="fin-stat expense">
          <span className="fin-stat-icon">📉</span>
          <p className="fin-stat-label">Total Expense</p>
          <p className="fin-stat-value">₹ {data.total_expense.toFixed(2)}</p>
        </div>
        <div className="fin-stat balance-final">
          <span className="fin-stat-icon">💳</span>
          <p className="fin-stat-label">Final Balance</p>
          <p className="fin-stat-value">₹ {data.balance.toFixed(2)}</p>
        </div>
      </div>

      {/* ══ DUAL TABLE ══ */}
      {loading ? (
        <div className="fin-empty">
          <div className="fin-empty-icon">⏳</div>
          <strong style={{ color:"#1e293b" }}>Loading entries…</strong>
        </div>
      ) : (
        <div className="fin-dual">

          {/* ── INCOME HALF ── */}
          <div className="fin-half income-half">
            <div className="fin-half-head">📈 Income</div>
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {incomes.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="fin-empty">
                        <div className="fin-empty-icon">📭</div>
                        <p>No income entries</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  incomes.map(row => (
                    <tr key={row.id}>
                      <td>{row.date}</td>
                      <td style={{ fontWeight:600 }}>{row.title}</td>
                      <td className="fin-amount-income">₹ {Number(row.amount).toFixed(2)}</td>
                      <td>
                        <button className="fin-btn-del" onClick={() => handleDelete(row.id)}>🗑</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {incomes.length > 0 && (
                <tfoot>
                  <tr className="fin-total-row">
                    <td colSpan="2" style={{ fontWeight:700 }}>Total</td>
                    <td className="fin-total-income">₹ {data.total_income.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* ── EXPENSE HALF ── */}
          <div className="fin-half expense-half">
            <div className="fin-half-head">📉 Expenses</div>
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="fin-empty">
                        <div className="fin-empty-icon">📭</div>
                        <p>No expense entries</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  expenses.map(row => (
                    <tr key={row.id}>
                      <td>{row.date}</td>
                      <td style={{ fontWeight:600 }}>{row.title}</td>
                      <td className="fin-amount-expense">₹ {Number(row.amount).toFixed(2)}</td>
                      <td>
                        <button className="fin-btn-del" onClick={() => handleDelete(row.id)}>🗑</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {expenses.length > 0 && (
                <tfoot>
                  <tr className="fin-total-row">
                    <td colSpan="2" style={{ fontWeight:700 }}>Total</td>
                    <td className="fin-total-expense">₹ {data.total_expense.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

        </div>
      )}
    </div>
  );
}
